import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import { Kbd } from '../../components/Kbd';
import { SkeletonText } from '../../components/Skeleton';
import { Tabs } from '../../components/Tabs';
import { formatDateTime } from '../../i18n';
import { hasModifier, useModifierLabel } from '../../lib/keyboard';
import { useSession } from '../auth/queries';
import { ArticleFiles } from './ArticleFiles';
import { ArticleForm } from './ArticleForm';
import { ArticleBody } from './ArticleBody';
import { ArticleVersions } from './ArticleVersions';
import type { ArticleVisibility, KbArticle } from './api';
import { toneForStatus, toneForVisibility } from './presentation';
import { templateSections, type KbSection } from './sections';
import { knowledgeKeys } from './queries';
import {
  useArticle,
  useArticleAction,
  useArticleAttachments,
  useCreateArticle,
  useUpdateArticle,
} from './queries';

/**
 * Ein Wissensartikel: lesen, bearbeiten, durch den Freigabelauf bringen.
 *
 * **Lesen ist der Normalzustand.** Das ist die eine Entscheidung, aus der
 * alles Weitere folgt -- und der Punkt, an dem die vorherige Fassung
 * grundsaetzlich falsch lag: Dort bekam jeder, der ``knowledge.write`` trug,
 * ausschliesslich das Formular zu sehen. Wer einen Artikel schrieb, hat ihn
 * nie gelesen; er kannte nur seine Rohfassung im Textfeld. Ein Wissensbestand,
 * dessen Verfasser das Ergebnis nicht kennen, verwahrlost, ohne dass es
 * jemandem auffaellt.
 *
 * Jetzt steht der Artikel da wie fuer den, der ihn spaeter braucht. Wer ihn
 * aendern will, klickt "Bearbeiten" und verlaesst diesen Zustand wieder.
 *
 * **Der Kopf klebt.** Beim Lesen bleibt sichtbar, welcher Artikel offen ist
 * und in welchem Stand er steht; beim Bearbeiten bleibt "Speichern"
 * erreichbar, ohne bis ans Ende einer langen Maske zu rollen.
 *
 * **Ungesicherte Aenderungen werden gemeldet und nicht stillschweigend
 * weggeworfen.** Die vorherige Fassung verwarf sie beim Klick auf einen
 * anderen Treffer -- ohne Nachfrage, ohne Hinweis, ohne Weg zurueck.
 * ``onDirtyChange`` meldet den Stand nach oben; die Seite fragt dann nach.
 *
 * **Der Aufbau nebenan.** Inhalt links, Eigenschaften rechts. Vorher lagen
 * Titel, sechs Abschnitte, Kategorie, Sichtbarkeit, Kundenzuordnung, Tags,
 * Prueffrist, Aenderungsvermerk, Freigabe, Anhaenge, Bewertung und Fassungen
 * in **einer** Spalte untereinander -- rund zwei Bildschirmhoehen, in denen
 * der Titel des Artikels laengst aus dem Bild war.
 */

/** Der bearbeitbare Zustand eines Artikels -- ein Gegenstand, keine zehn. */
export interface ArticleDraft {
  title: string;
  /** Nur der Altbestand: ein Artikel ohne Gliederung. */
  body: string;
  sections: KbSection[] | null;
  categoryId: string;
  visibility: ArticleVisibility;
  customerIds: string[];
  tagIds: string[];
  reviewDue: string;
  changeNote: string;
}

/** Was der Server geliefert hat, als bearbeitbarer Stand. */
function entwurfAus(article: KbArticle): ArticleDraft {
  return {
    title: article.title,
    body: article.body_html,
    sections: article.sections ?? null,
    categoryId: article.category_id ?? '',
    visibility: article.visibility,
    // Im Schema wahlfrei, weil es eine Vorgabe hat -- ein Artikel ohne
    // Zuordnung liefert eine leere Liste.
    customerIds: article.customer_ids ?? [],
    tagIds: article.tag_ids ?? [],
    // `datetime` -> `date`: Das Feld traegt einen Tag, kein Uhrzeitfenster.
    reviewDue: article.review_due_at ? article.review_due_at.slice(0, 10) : '',
    changeNote: '',
  };
}

function leererEntwurf(presetCustomerId?: string): ArticleDraft {
  return {
    title: '',
    body: '',
    sections: templateSections(),
    categoryId: '',
    visibility: 'internal',
    customerIds: presetCustomerId ? [presetCustomerId] : [],
    tagIds: [],
    reviewDue: '',
    changeNote: '',
  };
}

interface ArticlePaneProps {
  /** ``null`` heisst: ein Artikel, den es noch nicht gibt. */
  articleId: string | null;
  canWrite: boolean;
  editing: boolean;
  onEditingChange: (editing: boolean) => void;
  onSaved: (articleId: string) => void;
  onDirtyChange: (dirty: boolean) => void;
  presetCustomerId?: string | undefined;
}

export function ArticlePane({
  articleId,
  canWrite,
  editing,
  onEditingChange,
  onSaved,
  onDirtyChange,
  presetCustomerId,
}: ArticlePaneProps) {
  const { t, i18n } = useTranslation();
  const session = useSession();
  const modifier = useModifierLabel();
  const queryClient = useQueryClient();

  const query = useArticle(articleId);
  const attachments = useArticleAttachments(articleId);
  const create = useCreateArticle();
  const update = useUpdateArticle();
  const action = useArticleAction();

  const [draft, setDraft] = useState<ArticleDraft>(() => leererEntwurf(presetCustomerId));
  /** Der Stand, wie er vom Server kam -- die Vergleichsgrundlage fuer "geaendert". */
  const [baseline, setBaseline] = useState<ArticleDraft>(() => leererEntwurf(presetCustomerId));
  const [loadedId, setLoadedId] = useState<string | null>(null);

  const article = articleId === null ? null : (query.data ?? null);

  // Die Maske folgt dem geladenen Artikel -- **beim Wechsel**, nicht bei jeder
  // Antwort des Servers. Ohne diese Unterscheidung ueberschriebe jedes
  // Nachladen, was der Anwender gerade tippt.
  //
  // Als Anpassung waehrend des Rendervorgangs und nicht als Effekt: React
  // rechnet den Baum sofort neu und zeigt den Zwischenstand nie an. Ein
  // ``useEffect`` malte erst den alten Stand und danach den neuen.
  if (article && article.id !== loadedId) {
    const geladen = entwurfAus(article);
    setLoadedId(article.id);
    setDraft(geladen);
    setBaseline(geladen);
  } else if (articleId === null && loadedId !== null) {
    const leer = leererEntwurf(presetCustomerId);
    setLoadedId(null);
    setDraft(leer);
    setBaseline(leer);
  }

  // **Das Verlassen der Bearbeitung stellt den geladenen Stand wieder her.**
  // Ohne diese Zeile blieben verworfene Aenderungen im Entwurf stehen: Der
  // Artikel wechselt beim Klick auf denselben Treffer nicht, der Ladeblock
  // darueber greift also nicht -- und beim naechsten "Bearbeiten" staende
  // wieder da, was der Anwender gerade weggeworfen hat.
  const [warEditing, setWarEditing] = useState(editing);
  if (warEditing !== editing) {
    setWarEditing(editing);
    if (!editing) setDraft(baseline);
  }

  // "Geaendert" ist ein Vergleich und kein Merker: Wer ein Wort tippt und es
  // wieder loescht, hat nichts geaendert -- und soll dann auch nicht gefragt
  // werden, ob er es verwerfen will.
  const dirty = editing && JSON.stringify(draft) !== JSON.stringify(baseline);

  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  // Der geschlossene Reiter ist der haeufigste Verlust -- dieselbe Ueberlegung
  // wie bei den Beitragsentwuerfen (``lib/draft.ts``). Hier genuegt die
  // Rueckfrage des Browsers: Der Artikeltext gehoert dem Server, sobald er
  // gespeichert ist, und was noch nicht gespeichert ist, gehoert in kein
  // zweites Lager.
  useEffect(() => {
    const warnen = (event: BeforeUnloadEvent) => {
      if (!dirtyRef.current) return;
      // ``preventDefault`` allein genuegt seit langem; ``returnValue`` ist
      // abgekuendigt und traegt nichts mehr bei.
      event.preventDefault();
    };
    window.addEventListener('beforeunload', warnen);
    return () => {
      window.removeEventListener('beforeunload', warnen);
    };
  }, []);

  const speichern = () => {
    const gemeinsam = {
      title: draft.title.trim(),
      ...(draft.sections === null ? { body_html: draft.body } : { sections: draft.sections }),
      category_id: draft.categoryId || null,
      visibility: draft.visibility,
      customer_ids: draft.customerIds,
      tag_ids: draft.tagIds,
      review_due_at: draft.reviewDue ? `${draft.reviewDue}T00:00:00Z` : null,
    };

    // Was der Server zurueckgibt, ist der neue Ausgangsstand -- und zugleich
    // das, was die Leseansicht gleich zeigt. Beides muss gesetzt werden,
    // bevor die Bearbeitung endet: Sonst stellte die Zeile oben den Stand vor
    // dem Speichern wieder her, und die eigene Aenderung waere aus dem Bild.
    //
    // Der Artikel wandert ausserdem unmittelbar in den Zwischenspeicher der
    // Abfrage. Ohne das zeigte der Bereich nach dem Anlegen erst ein Skelett,
    // bis die entwertete Abfrage denselben Artikel noch einmal geholt hat.
    const uebernehmen = (saved: KbArticle) => {
      queryClient.setQueryData(knowledgeKeys.article(saved.id), saved);
      const stand = entwurfAus(saved);
      setLoadedId(saved.id);
      setDraft(stand);
      setBaseline(stand);
      setWarEditing(false);
      onEditingChange(false);
      onSaved(saved.id);
    };

    if (article) {
      update.mutate(
        { id: article.id, payload: { ...gemeinsam, change_note: draft.changeNote } },
        { onSuccess: uebernehmen },
      );
      return;
    }

    create.mutate(gemeinsam, { onSuccess: uebernehmen });
  };

  const speicherbar = draft.title.trim() !== '';
  const speichertGerade = create.isPending || update.isPending;

  // Das Kuerzel gilt nur, solange bearbeitet wird -- und es ist ein Kuerzel
  // mit Zusatztaste, weil es sonst im Titelfeld ein "s" ersetzte.
  useEffect(() => {
    if (!editing) return undefined;
    const beiTaste = (event: KeyboardEvent) => {
      if (!hasModifier(event) || event.key.toLowerCase() !== 's') return;
      event.preventDefault();
      if (speicherbar && !speichertGerade) speichern();
    };
    window.addEventListener('keydown', beiTaste);
    return () => {
      window.removeEventListener('keydown', beiTaste);
    };
    // ``speichern`` haengt am Entwurf und aendert sich bei jedem Tastendruck;
    // die Abhaengigkeiten bilden ab, was das Kuerzel zur Laufzeit braucht.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, speicherbar, speichertGerade, draft, article]);

  const canPublish = session.data?.permissions.includes('knowledge.publish') ?? false;

  if (articleId !== null && query.isPending) {
    return (
      <aside className="kb-detail" aria-busy="true">
        <div style={{ padding: 'var(--space-6)' }}>
          <SkeletonText lines={2} />
          <SkeletonText lines={6} />
        </div>
      </aside>
    );
  }
  if (query.isError) {
    return (
      <aside className="kb-detail">
        <div style={{ padding: 'var(--space-6)' }}>
          <ErrorNotice error={query.error} />
        </div>
      </aside>
    );
  }

  const patch = (teil: Partial<ArticleDraft>) => {
    setDraft((current) => ({ ...current, ...teil }));
  };

  const eigenschaften = (
    <ArticleForm
      draft={draft}
      onPatch={patch}
      editing={editing}
      hasArticle={article !== null}
      status={article?.status ?? null}
      canPublish={canPublish}
      actionBusy={action.isPending}
      onAction={(name) => {
        if (!article) return;
        action.mutate({ id: article.id, action: name });
      }}
      articleId={article?.id ?? null}
      ratingAverage={article?.rating_average ?? 0}
      ratingCount={article?.rating_count ?? 0}
    />
  );

  const inhalt = (
    <ArticleBody
      draft={draft}
      onPatch={patch}
      editing={editing}
      bodyFallbackHeading={t('knowledge.legacySection')}
    />
  );

  return (
    <aside className="kb-detail">
      <header className="kb-article__head">
        <div className="kb-article__title-row">
          <div>
            {editing ? (
              <input
                className="field__control"
                aria-label={t('knowledge.articleTitle')}
                placeholder={t('knowledge.titlePlaceholder')}
                value={draft.title}
                onChange={(event) => {
                  patch({ title: event.target.value });
                }}
              />
            ) : (
              <h2 className="kb-article__title">{draft.title || t('knowledge.newArticle')}</h2>
            )}

            <div className="kb-article__badges" style={{ marginTop: 'var(--space-2)' }}>
              {article ? (
                <Badge tone={toneForStatus(article.status)}>
                  {t(`kbStatus.${article.status}`)}
                </Badge>
              ) : (
                <Badge tone="neutral">{t('knowledge.notSavedYet')}</Badge>
              )}
              <Badge tone={toneForVisibility(draft.visibility)}>
                {t(`kbVisibility.${draft.visibility}`)}
              </Badge>
              <Badge tone={draft.customerIds.length === 0 ? 'neutral' : 'info'}>
                {draft.customerIds.length === 0
                  ? t('knowledge.customersAll')
                  : t('knowledge.customersSelected', { count: draft.customerIds.length })}
              </Badge>
            </div>
          </div>

          <div className="row">
            {editing ? (
              <>
                <Button
                  variant="ghost"
                  onClick={() => {
                    if (dirty && !window.confirm(t('knowledge.discardConfirm'))) return;
                    onEditingChange(false);
                    // Ein abgebrochener **neuer** Artikel hinterlaesst nichts;
                    // die Seite raeumt die Auswahl weg.
                    if (!article) onSaved('');
                  }}
                >
                  {t('knowledge.discard')}
                </Button>
                <Button
                  variant="primary"
                  disabled={!speicherbar}
                  busy={speichertGerade}
                  onClick={speichern}
                >
                  {t('app.save')}
                </Button>
              </>
            ) : canWrite ? (
              <Button
                variant="primary"
                onClick={() => {
                  onEditingChange(true);
                }}
              >
                {t('knowledge.edit')}
              </Button>
            ) : null}
          </div>
        </div>

        <div className="kb-article__meta">
          {article ? (
            <>
              <span>
                {t('knowledge.updatedAt', {
                  moment: formatDateTime(article.updated_at, i18n.language),
                })}
              </span>
              <span aria-hidden="true">·</span>
              <span>{t('knowledge.version', { number: article.current_version })}</span>
            </>
          ) : null}
          {editing ? (
            <>
              {article ? <span aria-hidden="true">·</span> : null}
              <span>
                <Kbd>{modifier}</Kbd>
                <Kbd>S</Kbd> {t('knowledge.savesHint')}
              </span>
            </>
          ) : null}
          {dirty ? (
            <span className="kb-article__dirty" role="status">
              {t('knowledge.unsaved')}
            </span>
          ) : null}
        </div>

        {create.isError ? <ErrorNotice error={create.error} /> : null}
        {update.isError ? <ErrorNotice error={update.error} /> : null}
        {action.isError ? <ErrorNotice error={action.error} /> : null}
      </header>

      <div className="kb-article__layout">
        <div className="kb-article__main">
          {article ? (
            <Tabs
              ariaLabel={t('knowledge.tabsLabel')}
              tabs={[
                { value: 'content', label: t('knowledge.tabContent'), content: inhalt },
                {
                  value: 'files',
                  label: t('knowledge.attachments'),
                  count: (attachments.data ?? []).length,
                  content: <ArticleFiles articleId={article.id} canWrite={canWrite} />,
                },
                {
                  value: 'versions',
                  label: t('knowledge.versions'),
                  content: (
                    <ArticleVersions
                      articleId={article.id}
                      currentVersion={article.current_version ?? null}
                    />
                  ),
                },
              ]}
            />
          ) : (
            // Ein Artikel, den es noch nicht gibt, hat weder Dateien noch
            // Fassungen. Leere Reiter anzubieten hiesse, zwei Wege ins Nichts
            // anzubieten -- eine Datei braucht etwas, woran sie haengt.
            inhalt
          )}
        </div>

        <div className="kb-props">{eigenschaften}</div>
      </div>
    </aside>
  );
}
